// Localização: frontend/src/components/Financeiro/GraficoContasReceber.jsx
// Gráfico de Contas a Receber

import React, { useState } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Card from '../UI/Card';
import CardContent from '../UI/CardContent';
import LoadingIndicator from '../Common/LoadingIndicator';
import Alert from '../UI/Alert';
import { DollarSign, BarChart3, LineChart as LineChartIcon } from 'lucide-react';

/**
 * @component GraficoContasReceber
 * @description Gráfico mostrando contas a receber agrupadas por período de vencimento
 * @param {Array} data - Dados das contas a receber agrupadas por período
 * @param {boolean} isLoading - Estado de carregamento
 * @param {string} error - Mensagem de erro
 * @param {string} periodo - Período selecionado (dia, semana, mes, custom)
 */
const GraficoContasReceber = ({ data = [], isLoading = false, error = null, periodo = 'mes' }) => {
    const [viewType, setViewType] = useState('barras'); // 'barras' ou 'linha'

    // Função para formatar o período baseado no tipo
    const formatarPeriodo = (periodoStr) => {
        if (!periodoStr) return 'Período';
        
        // Se for formato de data (YYYY-MM-DD)
        if (/^\d{4}-\d{2}-\d{2}$/.test(periodoStr)) {
            const date = new Date(periodoStr + 'T00:00:00');
            if (periodo === 'dia') {
                return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
            }
            return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
        }
        
        // Se for formato de semana ISO (IYYY-IW do PostgreSQL) - formato: YYYY-WW
        if (/^\d{4}-\d{1,2}$/.test(periodoStr) && periodo === 'semana') {
            const parts = periodoStr.split('-');
            const week = parseInt(parts[1]);
            const year = parts[0];
            if (week >= 1 && week <= 53) {
                return `Sem ${week}/${year}`;
            }
        }
        
        // Se for formato de mês (YYYY-MM)
        if (/^\d{4}-\d{2}$/.test(periodoStr) && periodo === 'mes') {
            const [year, month] = periodoStr.split('-');
            const date = new Date(parseInt(year), parseInt(month) - 1, 1);
            return date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
        }
        
        // Se for formato de semana ISO com W (YYYY-WW)
        if (periodoStr.includes('-W')) {
            const [year, week] = periodoStr.split('-W');
            return `Sem ${week}/${year}`;
        }
        
        // Fallback: tentar detectar se é semana pelo período selecionado
        if (/^\d{4}-\d{1,2}$/.test(periodoStr)) {
            const parts = periodoStr.split('-');
            if (periodo === 'semana' && parseInt(parts[1]) <= 53) {
                return `Sem ${parts[1]}/${parts[0]}`;
            }
            if (periodo === 'mes' && parseInt(parts[1]) <= 12) {
                const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, 1);
                return date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
            }
        }
        
        return periodoStr;
    };

    if (isLoading) {
        return (
            <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <CardContent className="pt-6 pb-6 px-6">
                    <div className="flex items-center gap-3 mb-4">
                        <DollarSign className="w-6 h-6 text-emerald-600" />
                        <h3 className="text-xl font-bold text-gray-800">Receitas</h3>
                    </div>
                    <div className="py-12">
                        <LoadingIndicator message="Carregando dados de receitas..." />
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <CardContent className="pt-6 pb-6 px-6">
                    <div className="flex items-center gap-3 mb-4">
                        <DollarSign className="w-6 h-6 text-emerald-600" />
                        <h3 className="text-xl font-bold text-gray-800">Receitas</h3>
                    </div>
                    <div className="py-4">
                        <Alert type="error" message={error} />
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <CardContent className="pt-6 pb-6 px-6">
                    <div className="flex items-center gap-3 mb-4">
                        <DollarSign className="w-6 h-6 text-emerald-600" />
                        <h3 className="text-xl font-bold text-gray-800">Receitas</h3>
                    </div>
                    <div className="h-96 flex flex-col items-center justify-center py-8">
                        <DollarSign className="h-16 w-16 text-gray-300 mb-4" />
                        <p className="text-gray-500 text-lg">Sem dados para exibir</p>
                        <p className="text-gray-400 text-sm mt-2">Não há receitas no período selecionado</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    // Formatar dados para o gráfico
    const chartData = data.map(item => ({
        periodo: formatarPeriodo(item.periodo || item.data_vencimento),
        periodoOriginal: item.periodo || item.data_vencimento || 'Período',
        pendentes: parseFloat(item.pendentes || item.valor_pendente || 0),
        recebidas: parseFloat(item.recebidas || item.valor_recebido || 0),
        vencidas: parseFloat(item.vencidas || item.valor_vencido || 0),
        total: parseFloat(item.total || item.valor_total || 0)
    }));

    // Calcular totais
    const totalPendentes = chartData.reduce((sum, item) => sum + item.pendentes, 0);
    const totalRecebidas = chartData.reduce((sum, item) => sum + item.recebidas, 0);
    const totalVencidas = chartData.reduce((sum, item) => sum + item.vencidas, 0);
    const totalGeral = chartData.reduce((sum, item) => sum + item.total, 0);

    // Tooltip customizado
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0]?.payload;
            const periodoCompleto = data?.periodoOriginal || label;
            
            return (
                <div className="bg-white p-4 border border-gray-200 shadow-xl rounded-lg text-sm backdrop-blur-sm">
                    <p className="font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">{periodoCompleto}</p>
                    <div className="space-y-2">
                        {payload.map((entry, index) => (
                            <div key={index} className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                    <div 
                                        className="w-3 h-3 rounded-full" 
                                        style={{ backgroundColor: entry.color }}
                                    ></div>
                                    <span className="text-gray-600">{entry.name}:</span>
                                </div>
                                <span className="font-semibold" style={{ color: entry.color }}>
                                    {new Intl.NumberFormat('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL'
                                    }).format(entry.value)}
                                </span>
                            </div>
                        ))}
                        <div className="pt-2 mt-2 border-t border-gray-200">
                            <div className="flex items-center justify-between gap-4">
                                <span className="font-semibold text-gray-700">Total:</span>
                                <span className="font-bold text-gray-800">
                                    {new Intl.NumberFormat('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL'
                                    }).format(data?.total || 0)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1 h-full flex flex-col">
            <CardContent className="pt-6 pb-6 px-6 h-full flex flex-col">
                {/* Header com Toggle */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-emerald-100 text-emerald-600">
                            <DollarSign className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-800">Receitas</h3>
                            <p className="text-sm text-gray-500">
                                Análise de receitas por período de vencimento
                            </p>
                        </div>
                    </div>
                    
                    {/* Toggle de Visualização */}
                    <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
                        <button
                            onClick={() => setViewType('barras')}
                            className={`px-4 py-2 text-sm rounded-md font-medium transition-all duration-200 flex items-center gap-2 ${
                                viewType === 'barras'
                                    ? 'bg-white text-emerald-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800'
                            }`}
                        >
                            <BarChart3 className="w-4 h-4" />
                            Barras
                        </button>
                        <button
                            onClick={() => setViewType('linha')}
                            className={`px-4 py-2 text-sm rounded-md font-medium transition-all duration-200 flex items-center gap-2 ${
                                viewType === 'linha'
                                    ? 'bg-white text-emerald-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800'
                            }`}
                        >
                            <LineChartIcon className="w-4 h-4" />
                            Linha
                        </button>
                    </div>
                </div>

                {/* Cards de Resumo */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4 border border-yellow-200">
                        <div className="flex items-center gap-2 mb-2">
                            <DollarSign className="w-4 h-4 text-yellow-600" />
                            <p className="text-xs font-semibold text-yellow-700">Pendentes</p>
                        </div>
                        <p className="text-xl font-bold text-yellow-600">
                            {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                                notation: 'compact',
                                maximumFractionDigits: 1
                            }).format(totalPendentes)}
                        </p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-4 border border-emerald-200">
                        <div className="flex items-center gap-2 mb-2">
                            <DollarSign className="w-4 h-4 text-emerald-600" />
                            <p className="text-xs font-semibold text-emerald-700">Recebidas</p>
                        </div>
                        <p className="text-xl font-bold text-emerald-600">
                            {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                                notation: 'compact',
                                maximumFractionDigits: 1
                            }).format(totalRecebidas)}
                        </p>
                    </div>
                    <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
                        <div className="flex items-center gap-2 mb-2">
                            <DollarSign className="w-4 h-4 text-red-600" />
                            <p className="text-xs font-semibold text-red-700">Vencidas</p>
                        </div>
                        <p className="text-xl font-bold text-red-600">
                            {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                                notation: 'compact',
                                maximumFractionDigits: 1
                            }).format(totalVencidas)}
                        </p>
                    </div>
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center gap-2 mb-2">
                            <DollarSign className="w-4 h-4 text-gray-600" />
                            <p className="text-xs font-semibold text-gray-700">Total</p>
                        </div>
                        <p className="text-xl font-bold text-gray-600">
                            {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                                notation: 'compact',
                                maximumFractionDigits: 1
                            }).format(totalGeral)}
                        </p>
                    </div>
                </div>

                {/* Gráfico */}
                <div className="outline-none flex-1" style={{ outline: 'none', marginBottom: '0px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        {viewType === 'barras' ? (
                            <BarChart 
                                data={chartData} 
                                margin={{ top: 20, right: 30, left: 20, bottom: 0 }}
                            >
                                <defs>
                                    <linearGradient id="colorPendentesReceber" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#eab308" stopOpacity={1} />
                                        <stop offset="100%" stopColor="#ca8a04" stopOpacity={0.8} />
                                    </linearGradient>
                                    <linearGradient id="colorRecebidas" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#10b981" stopOpacity={1} />
                                        <stop offset="100%" stopColor="#059669" stopOpacity={0.8} />
                                    </linearGradient>
                                    <linearGradient id="colorVencidasReceber" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#ef4444" stopOpacity={1} />
                                        <stop offset="100%" stopColor="#dc2626" stopOpacity={0.8} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid 
                                    strokeDasharray="3 3" 
                                    stroke="#e5e7eb" 
                                    vertical={false}
                                />
                                <XAxis 
                                    dataKey="periodo" 
                                    stroke="#6b7280"
                                    style={{ fontSize: '12px' }}
                                    angle={periodo === 'dia' ? -45 : -30}
                                    textAnchor={periodo === 'dia' ? 'end' : 'end'}
                                    height={periodo === 'dia' ? 80 : 60}
                                    tick={{ fill: '#6b7280' }}
                                    axisLine={{ stroke: '#6b7280', strokeWidth: 1 }}
                                    tickLine={{ stroke: '#6b7280' }}
                                />
                                <YAxis 
                                    stroke="#6b7280"
                                    tick={{ fill: '#6b7280' }}
                                    tickFormatter={(value) => 
                                        new Intl.NumberFormat('pt-BR', {
                                            style: 'currency',
                                            currency: 'BRL',
                                            notation: 'compact'
                                        }).format(value)
                                    }
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend 
                                    wrapperStyle={{ paddingTop: '20px' }}
                                    iconType="circle"
                                />
                                <Bar 
                                    dataKey="pendentes" 
                                    fill="url(#colorPendentesReceber)" 
                                    name="Pendentes"
                                    radius={[8, 8, 0, 0]}
                                    animationDuration={800}
                                />
                                <Bar 
                                    dataKey="recebidas" 
                                    fill="url(#colorRecebidas)" 
                                    name="Recebidas"
                                    radius={[8, 8, 0, 0]}
                                    animationDuration={800}
                                />
                                <Bar 
                                    dataKey="vencidas" 
                                    fill="url(#colorVencidasReceber)" 
                                    name="Vencidas"
                                    radius={[8, 8, 0, 0]}
                                    animationDuration={800}
                                />
                            </BarChart>
                        ) : (
                            <LineChart 
                                data={chartData} 
                                margin={{ top: 20, right: 30, left: 20, bottom: 0 }}
                            >
                                <defs>
                                    <linearGradient id="gradientPendentesReceber" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#eab308" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#eab308" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="gradientRecebidas" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="gradientVencidasReceber" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid 
                                    strokeDasharray="3 3" 
                                    stroke="#e5e7eb" 
                                    vertical={false}
                                />
                                <XAxis 
                                    dataKey="periodo" 
                                    stroke="#6b7280"
                                    style={{ fontSize: '12px' }}
                                    angle={periodo === 'dia' ? -45 : -30}
                                    textAnchor={periodo === 'dia' ? 'end' : 'end'}
                                    height={periodo === 'dia' ? 80 : 60}
                                    tick={{ fill: '#6b7280' }}
                                    axisLine={{ stroke: '#6b7280', strokeWidth: 1 }}
                                    tickLine={{ stroke: '#6b7280' }}
                                />
                                <YAxis 
                                    stroke="#6b7280"
                                    tick={{ fill: '#6b7280' }}
                                    tickFormatter={(value) => 
                                        new Intl.NumberFormat('pt-BR', {
                                            style: 'currency',
                                            currency: 'BRL',
                                            notation: 'compact'
                                        }).format(value)
                                    }
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend 
                                    wrapperStyle={{ paddingTop: '20px' }}
                                    iconType="circle"
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="pendentes" 
                                    stroke="#eab308" 
                                    strokeWidth={3}
                                    name="Pendentes"
                                    dot={{ fill: '#eab308', r: 5, strokeWidth: 2, stroke: '#fff' }}
                                    activeDot={{ r: 7, strokeWidth: 2, stroke: '#fff' }}
                                    animationDuration={800}
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="recebidas" 
                                    stroke="#10b981" 
                                    strokeWidth={3}
                                    name="Recebidas"
                                    dot={{ fill: '#10b981', r: 5, strokeWidth: 2, stroke: '#fff' }}
                                    activeDot={{ r: 7, strokeWidth: 2, stroke: '#fff' }}
                                    animationDuration={800}
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="vencidas" 
                                    stroke="#ef4444" 
                                    strokeWidth={3}
                                    name="Vencidas"
                                    dot={{ fill: '#ef4444', r: 5, strokeWidth: 2, stroke: '#fff' }}
                                    activeDot={{ r: 7, strokeWidth: 2, stroke: '#fff' }}
                                    animationDuration={800}
                                />
                            </LineChart>
                        )}
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    );
};

export default GraficoContasReceber;

